import React, { useMemo, useState } from "react";
import Th from "./Th";
import Td from "./Td";
import CardTitle from "./CardTitle";
import StatusBadge from "./StatusBadge";
import GroupedTripLink from "./GroupedTripLink";
import Pagination from "./Pagination";
import SearchBox from "./SearchBox";
import useTripSearch from "./useTripSearch";
import { formatDate } from "./helpers";
import { CARD_SURFACE, PAGE_SIZE } from "./constants";

// Module-level so useTripSearch's memo keeps a stable identity.
const SEARCH_FIELDS = [];

const GroupedTripsTable = ({ groupedTrips }) => {
  const [sortKey, setSortKey] = useState("occurred_at");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(1);
  const [filtered, search, setSearch] = useTripSearch(groupedTrips, SEARCH_FIELDS);

  const sorted = useMemo(() => {
    const copy = [...filtered];
    copy.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (av == null) return 1;
      if (bv == null) return -1;
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return copy;
  }, [filtered, sortKey, sortDir]);

  const pageCount = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  // `page` is raw user state; clamp on render so a shrinking dataset (e.g. after
  // a search) doesn't need an effect-driven reset.
  const safePage = Math.min(page, pageCount);
  const pageRows = sorted.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const onSort = (key) => {
    if (key === sortKey) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  return (
    <section className="bg-white overflow-hidden" style={CARD_SURFACE}>
      <div className="px-6 py-4 flex justify-between items-center" style={{ gap: 16 }}>
        <CardTitle>Recent Trips Activity ({groupedTrips.length})</CardTitle>
        <SearchBox value={search} onChange={setSearch} />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead className="border-b border-gray-200">
            <tr>
              <Th>Grouped Trip ID</Th>
              <Th onClick={() => onSort("occurred_at")} active={sortKey === "occurred_at"} dir={sortDir}>
                Date &amp; Time
              </Th>
              <Th onClick={() => onSort("duration_min")} active={sortKey === "duration_min"} dir={sortDir} align="right">
                Duration
              </Th>
              <Th onClick={() => onSort("distance_km")} active={sortKey === "distance_km"} dir={sortDir} align="right">
                Distance
              </Th>
              <Th onClick={() => onSort("status")} active={sortKey === "status"} dir={sortDir}>
                Status
              </Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {pageRows.length === 0 ? (
              <tr>
                <td colSpan="5" className="px-4 py-6 text-center text-sm text-gray-500 italic">
                  {search ? "No trips match your search." : "No trips this week."}
                </td>
              </tr>
            ) : (
              pageRows.map((groupedTrip) => (
                <tr key={groupedTrip.id} className="hover:bg-gray-100">
                  <Td>
                    <GroupedTripLink id={groupedTrip.id} />
                  </Td>
                  <Td>{formatDate(groupedTrip.occurred_at)}</Td>
                  <Td align="right">{groupedTrip.duration_min} mins</Td>
                  <Td align="right">{groupedTrip.distance_km?.toFixed(1)} km</Td>
                  <Td plain>
                    <StatusBadge status={groupedTrip.status} />
                  </Td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {pageCount > 1 && (
        <Pagination page={safePage} pageCount={pageCount} onChange={setPage} total={sorted.length} />
      )}
    </section>
  );
};

export default GroupedTripsTable;
